import { FC, useMemo, useState } from 'react';

import { getShopOrderCsMemoLogAll } from '@shared/api/shopOrderCsMemo/shopOrderCsMemo.controller';
import {
  useShopOrderCsMemoLogAllQuery,
  useShopOrderCsMemoLogQuery,
  useShopOrderCsMemoLogSummaryQuery,
} from '@shared/api/shopOrderCsMemo/shopOrderCsMemo.hook';
import {
  ShopOrderCsMemoLogItem,
  ShopOrderCsMemoLogParams,
} from '@shared/api/shopOrderCsMemo/shopOrderCsMemo.interface';
import {
  CellDetailPopover,
  CsMemoLogStatsPanel,
  CsMemoModalBase,
  isRoleBadgeRedundant,
} from '@shared/components/csMemo';
import { Loading } from '@shared/components/Loading';
import { BucketImage } from '@shared/components/BucketImage';
import { DeathCharacter } from '@shared/miscValues';
import { toastError, toastSuccess } from '@shared/plugIn/toast';
import { useAuth } from '@shared/state/hooks/useAuth';
import { orderBrandName, shortBrandName } from '@shared/utils/bugoBrandName.util';
import { buildCsMemoStats } from '@shared/utils/csMemoStats';
import { formatPrice } from '@shared/utils/formatPrice';
import {
  deliveryStatusBadge,
  deliveryStatusLabel,
  paymentStatusBadge,
  paymentStatusLabel,
} from '@shared/utils/orderStatusLabel';
import { bareReceiverName } from '@shared/utils/receiverName.util';
import {
  amountBreakdown,
  cancelProgress,
  deliveryDateInfo,
  refundRouteLabel,
  toShopCsMemoStatRows,
} from '@shared/utils/shopOrderCsSummary';

import { shopOrderCsMemoAdapter } from '../components/csMemo';
import { useSellerTopNavHook } from '../hooks/useSellerTopNavHook';
import { exportShopCsMemoLogExcel } from '../utils/shopCsMemoLogExcel';

//* 끌 수 있는 열 — 화면 폭은 사람마다 달라서 "다 넣기"보다 "고르게"가 맞다.
//* 작성일시·결제·배송·응대 내용·이력은 이 표의 뼈대라 목록에서 뺐다(끌 수 없음).
/** 요약 칩 한 칸 — [라벨, 건수] */
type Bucket = [string, number];

const TOGGLE_COLUMNS = [
  { key: 'phrase', label: '리본 문구' },
  { key: 'order', label: '주문' },
  { key: 'deliveryPlace', label: '상가' },
  { key: 'brand', label: '상조' },
  { key: 'photo', label: '사진' },
  { key: 'amount', label: '금액' },
] as const;

type ToggleColumnKey = typeof TOGGLE_COLUMNS[number]['key'];

const HIDDEN_COLUMNS_KEY = 'shopCsMemoLog.hiddenColumns';

const loadHiddenColumns = (): ToggleColumnKey[] => {
  try {
    const raw = localStorage.getItem(HIDDEN_COLUMNS_KEY);
    return raw ? (JSON.parse(raw) as ToggleColumnKey[]) : [];
  } catch {
    return [];
  }
};

const pad = (n: number) => String(n).padStart(2, '0');
const toDateInput = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateInput(d);
};

//* 올해 건이면 연도를 생략한다 — 로그는 대부분 최근 건이고, `26.`이 열마다 붙으면
//* 그것만으로 표가 한 화면을 넘는다. 지난해 건은 연도를 남겨 오독을 막는다.
const thisYear = new Date().getFullYear();
const ymd = (d: Date) =>
  d.getFullYear() === thisYear
    ? `${pad(d.getMonth() + 1)}.${pad(d.getDate())}`
    : `${String(d.getFullYear()).slice(2)}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;

const fmtDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${ymd(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

//* 주문 요약 줄에선 시:분까지 필요 없다 — 표가 길어지기만 한다
const fmtDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  return ymd(new Date(date));
};

//* 보내는 분 이름은 리본 문구와 사실상 같은 값이 들어온다("주)메디플로 이창혁 대표").
//* 문구 열에 전문이 이미 있으니 표에서는 앞 5글자로 자른다 — 이 한 줄이 주문 열 폭을 좌우한다.
//* 전체 이름은 title로 남기고, 문구 열·모달·엑셀은 그대로 전문이다.
const clipSenderName = (name: string | undefined, max = 5) => {
  if (!name) return '';
  return name.length > max ? `${name.slice(0, max)}…` : name;
};

//* 주문 식별용 짧은 번호 — 서버가 orderNo로 주는 게 정답이고, 안 오면 프로젝트 관례대로 직접 자른다
//* (OrderManualRefundRequestModal·DeliveryManageCard 등과 동일한 뒤 6자리 규칙)
const shortOrderNo = (item: ShopOrderCsMemoLogItem) =>
  item.order?.orderNo ?? item.shopOrder.slice(-6);

//* 배송지 = 고인명 / 장례식장 · 호실. 전화 응대는 "김OO 어르신 상가"로 들어오므로 고인명이 1순위 식별 키다.
//* 전체 주소는 표에 넣지 않고 모달·엑셀에만 쓴다.
const deliveryPlaceLine = (order: { funeralHomeName?: string; roomName?: string }) =>
  [order.funeralHomeName, order.roomName].filter(Boolean).join(' · ');

//* 메모장 모달 헤더 — 응대는 여기서 일어나므로 표보다 두텁게 요약한다.
//* 금액은 합계가 아니라 분해해서(원 + P) 보여준다 — 환불 경로가 현금분으로 갈리기 때문.
//* (docs/backend-request-2026-08-14.md §1-1, §5-4)
const orderSummaryLine = (item: ShopOrderCsMemoLogItem) => {
  const order = item.order;
  if (!order) return undefined;
  const cancel = cancelProgress(order);
  const delivery = deliveryDateInfo(order);
  return [
    `주문 #${shortOrderNo(item)}`,
    orderBrandName(order),
    `보내는 분 ${order.senderName || '-'}`,
    `받는 분 ${bareReceiverName(order.receiverName) || '-'}`,
    order.deceasedName && `${order.deceasedName}님 상가`,
    deliveryPlaceLine(order),
    order.receiverAddress,
    order.itemName,
    order.quantity && `${order.quantity}개`,
    //* 리본 문구 — 오기입 문의의 실제 대상
    order.senderPhrase && `문구: ${order.senderPhrase}`,
    order.condolencePhrase,
    amountBreakdown(order),
    order.method,
    refundRouteLabel(order),
    order.paymentStatus && `결제 ${paymentStatusLabel(order.paymentStatus)}`,
    order.deliveryStatus && `배송 ${deliveryStatusLabel(order.deliveryStatus)}`,
    order.purchasedAt && `결제 ${fmtDate(order.purchasedAt)}`,
    delivery &&
      `${delivery.estimated ? '배송예정' : '배송완료'} ${fmtDate(delivery.date)}`,
    cancel && `${cancel.label} ${fmtDate(cancel.date)}`,
    order.cancelReason && `취소사유: ${order.cancelReason}`,
    //* 단가×수량과 실결제액이 어긋나는 정당한 사유 — 그 경위가 문장으로 저장돼 있다
    order.hasItemChange && '상품 교체 있음',
    order.paymentMemo,
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * 화환 CS 응대 로그 — 남긴 응대 메모를 기간별로 훑는 표.
 * 셀러는 자기 매장 메모만(서버가 거른다), 관리자 전용 메모도 서버가 셀러 응답에서 뺀다.
 */
export const SellerCsMemoLogPage: FC = function SellerCsMemoLogPage() {
  useSellerTopNavHook('CS 응대 로그');
  const { isAdmin } = useAuth();
  const admin = isAdmin();

  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(toDateInput(new Date()));
  const [adminOnly, setAdminOnly] = useState(false);
  //* 기본 20 — 50건은 한 화면에서 훑기엔 스크롤이 너무 길다 (아래 셀렉트로 조절)
  const [limit, setLimit] = useState(20);
  // 입력 중인 검색어와 실제 조회에 쓰는 검색어를 분리 (타이핑마다 조회하지 않는다)
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [modalItem, setModalItem] = useState<ShopOrderCsMemoLogItem | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  //* 통계는 조회 조건 전체를 다시 긁어와야 해서 **열었을 때만** 돈다
  const [showStats, setShowStats] = useState(false);
  const [hiddenColumns, setHiddenColumns] =
    useState<ToggleColumnKey[]>(loadHiddenColumns);
  const [showColumnMenu, setShowColumnMenu] = useState(false);

  const toggleColumn = (key: ToggleColumnKey) => {
    setHiddenColumns((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      //* 다음에 들어와도 같은 화면이어야 한다 (사람마다 모니터가 다르다)
      try {
        localStorage.setItem(HIDDEN_COLUMNS_KEY, JSON.stringify(next));
      } catch {
        // 저장 실패는 무시 — 이번 세션에서만 적용된다
      }
      return next;
    });
  };
  // 표의 주문번호를 눌러 그 주문 하나만 보는 상태 — 검색어를 거치지 않는 정확한 경로.
  // (q의 6자 접미사 매칭은 유일성이 없어 다른 주문이 섞일 수 있다)
  const [orderFilter, setOrderFilter] = useState<{ id: string; no: string } | null>(null);

  const params = useMemo<ShopOrderCsMemoLogParams>(
    () => ({
      //* shopOrder를 주면 서버가 기간을 무시하고 그 주문의 전체 이력을 준다 —
      //* 기간을 같이 보내면 "왜 기간을 바꿔도 결과가 같지"로 읽혀서 아예 빼고 보낸다
      ...(orderFilter ? { shopOrder: orderFilter.id } : { from, to }),
      ...(query ? { q: query } : {}),
      ...(admin && adminOnly ? { adminOnly: true } : {}),
      page,
      limit,
    }),
    [orderFilter, from, to, query, admin, adminOnly, page, limit],
  );

  const filterByOrder = (item: ShopOrderCsMemoLogItem) => {
    setOrderFilter({ id: item.shopOrder, no: shortOrderNo(item) });
    setPage(1);
  };

  const { data, isLoading, isFetching } = useShopOrderCsMemoLogQuery(params);
  //* 분포 칩 — 서버 집계 1요청. 미배포면 null이라 아래에서 페이지 기준으로 근사한다
  const { data: summary } = useShopOrderCsMemoLogSummaryQuery(params);
  const serverDist = summary ?? null;
  const unavailable = data === null;
  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  //* 전량 수집(200건 × 최대 10요청)은 **통계를 열었을 때만**. 예전엔 칩 몇 줄을 그리려고
  //* 600건 이하면 진입하자마자 전량을 끌어왔는데, 그 요청 하나가 서버에선 대상 요약 조인까지
  //* 10여 쿼리라 진입 때마다 부하가 곱해졌다 (답례품 로그와 같은 구조·같은 처방).
  const { data: allData, isLoading: isStatsLoading } = useShopOrderCsMemoLogAllQuery(
    params,
    showStats,
  );
  //* 전량을 안 모은 동안에는 페이지 기준으로 근사하고 라벨로 그렇게 밝힌다
  const summaryItems = allData?.items ?? items;
  const summaryScope = serverDist || allData ? '' : '이 페이지 ';

  // 확장 필드는 백엔드 배포 시차·레거시 주문 때문에 비어 있을 수 있다. 전부 '-'인 죽은 열을
  // 띄우지 않도록, 한 건이라도 값이 있을 때만 해당 열을 렌더한다.
  // 백엔드가 반영되면 프론트 배포 없이 열이 살아난다.
  //* 열은 두 조건을 다 만족해야 뜬다 — ① 값이 하나라도 있고(죽은 열 방지)
  //* ② 사용자가 끄지 않았을 것. 서버가 값을 주기 시작하면 ①은 자동으로 켜진다.
  const columns = useMemo(() => {
    const has: Record<ToggleColumnKey, boolean> = {
      deliveryPlace: items.some(
        (item) =>
          !!(
            item.order?.deceasedName ||
            item.order?.funeralHomeName ||
            item.order?.roomName
          ),
      ),
      brand: items.some((item) => !!orderBrandName(item.order)),
      photo: items.some((item) => !!item.order?.deliveredImageUrl),
      amount: items.some((item) => item.order?.amount !== undefined),
      phrase: items.some(
        (item) => !!(item.order?.senderPhrase || item.order?.condolencePhrase),
      ),
      order: true,
    };
    return TOGGLE_COLUMNS.reduce(
      (acc, { key }) => ({ ...acc, [key]: has[key] && !hiddenColumns.includes(key) }),
      {} as Record<ToggleColumnKey, boolean>,
    );
  }, [items, hiddenColumns]);

  // 서버가 memoCount(기간·페이지 무관 전체 건수)를 주면 그걸 쓰고, 아직 안 오면 지금 보고 있는
  // 페이지에서 세어 근사한다. 두 값의 의미가 달라서 툴팁 문구도 같이 갈린다.
  const pageMemoCountByOrder = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of items) map[item.shopOrder] = (map[item.shopOrder] ?? 0) + 1;
    return map;
  }, [items]);

  // 분포는 조회 결과 전체 기준(서버 집계가 있으면 그걸, 없으면 손에 든 것으로 근사 —
  // 어느 쪽인지는 `summaryScope` 라벨이 밝힌다)
  const writerCounts = useMemo<Bucket[]>(() => {
    if (serverDist) {
      return serverDist.byWriter
        .map((b): Bucket => [b.label, b.count])
        .sort((a, b) => b[1] - a[1]);
    }
    const map = new Map<string, number>();
    for (const item of summaryItems) {
      map.set(item.byName, (map.get(item.byName) ?? 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [serverDist, summaryItems]);

  // 표를 훑기 전에 "어떤 상태에서 문의가 나오나"를 본다.
  // 두 축을 한 줄에 섞지 않는다(결제취소인데 배송완료 같은 조합이 실제로 있다)
  const statusCounts = useMemo<{ payment: Bucket[]; delivery: Bucket[] }>(() => {
    const top = (map: Map<string, number>) =>
      Array.from(map.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4);

    if (serverDist) {
      //* 서버는 원시 enum으로 준다 — 라벨은 표와 **같은 규칙**으로 프론트가 만든다
      const fold = (
        buckets: { status: string; count: number }[],
        label: (status: string) => string,
      ) => {
        const map = new Map<string, number>();
        for (const bucket of buckets) {
          const key = label(bucket.status);
          map.set(key, (map.get(key) ?? 0) + bucket.count);
        }
        return top(map);
      };
      return {
        payment: fold(serverDist.byPaymentStatus, paymentStatusLabel),
        delivery: fold(serverDist.byDeliveryStatus, deliveryStatusLabel),
      };
    }

    const payment = new Map<string, number>();
    const delivery = new Map<string, number>();
    for (const item of summaryItems) {
      if (item.order?.paymentStatus) {
        const label = paymentStatusLabel(item.order.paymentStatus);
        payment.set(label, (payment.get(label) ?? 0) + 1);
      }
      if (item.order?.deliveryStatus) {
        const label = deliveryStatusLabel(item.order.deliveryStatus);
        delivery.set(label, (delivery.get(label) ?? 0) + 1);
      }
    }
    return { payment: top(payment), delivery: top(delivery) };
  }, [serverDist, summaryItems]);

  // 상조 분포 — "웅진프리드라이프 건이 몇 건인지"를 표 훑기 전에
  const brandCounts = useMemo<Bucket[]>(() => {
    if (serverDist) {
      return serverDist.byBrand
        .map((b): Bucket => [b.label, b.count])
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);
    }
    const map = new Map<string, number>();
    for (const item of summaryItems) {
      const name = orderBrandName(item.order);
      if (name) map.set(name, (map.get(name) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [serverDist, summaryItems]);

  const stats = useMemo(
    () =>
      allData
        ? buildCsMemoStats(
            toShopCsMemoStatRows(allData.items, {
              paymentStatus: paymentStatusLabel,
              deliveryStatus: deliveryStatusLabel,
            }),
            allData.total,
          )
        : null,
    [allData],
  );

  const applyRange = (fromDate: string) => {
    setFrom(fromDate);
    setTo(toDateInput(new Date()));
    setPage(1);
  };

  const runSearch = () => {
    setQuery(searchInput.trim());
    setPage(1);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      //* 통계 패널과 같은 수집 함수 — 두 화면이 다른 모집단을 보지 않게
      const { items: all, capped } = await getShopOrderCsMemoLogAll(params);
      if (all.length === 0) {
        toastError('내보낼 메모가 없습니다.');
        return;
      }
      exportShopCsMemoLogExcel(all, { from, to, orderNo: orderFilter?.no });
      if (capped) {
        toastSuccess(
          `${all.length}건 내보냈습니다. (한 번에 최대 2,000건 — 기간을 나눠 받아주세요)`,
        );
      }
    } catch {
      toastError('엑셀 내보내기에 실패했습니다.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    // 열이 10개까지 늘어서 max-w-6xl(1152px)로는 표가 짜부라진다 (내용이 한 글자씩 쪼개짐).
    // 상단바(SellerTopNavBar)는 셀러 페이지 전체가 공유하는 컴포넌트라 6xl 그대로 두었다 —
    // 이 화면만 본문이 더 넓어 헤더와 좌우가 어긋나는 건 알고 있는 상태(폭 정책 논의 후 정리 예정).
    <div className="mx-auto max-w-screen-2xl px-4 pb-8 sm:px-6 lg:px-8">
      {/* 필터 */}
      <div className="mt-6 space-y-2 rounded-lg bg-white p-4 shadow">
        {/* 특정 주문만 보는 모드 — 서버가 기간을 무시하므로 기간 입력을 잠가 오해를 막는다 */}
        {orderFilter && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-cyan-50 px-3 py-2 text-xs text-cyan-800">
            <span className="font-semibold">주문 #{orderFilter.no}</span>
            <span>의 응대 이력 전체 — 조회 기간과 무관하게 모두 보여줍니다.</span>
            <button
              type="button"
              onClick={() => {
                setOrderFilter(null);
                setPage(1);
              }}
              className="rounded-md border border-cyan-300 bg-white px-2 py-0.5 font-medium hover:bg-cyan-100"
            >
              기간 조회로 돌아가기
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={from}
            disabled={!!orderFilter}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
          />
          <span className="text-gray-400">~</span>
          <input
            type="date"
            value={to}
            disabled={!!orderFilter}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
          />
          <div className="flex items-center gap-1">
            {[
              { label: '오늘', days: 0 },
              { label: '7일', days: 7 },
              { label: '30일', days: 30 },
              { label: '90일', days: 90 },
            ].map((preset) => (
              <button
                key={preset.label}
                type="button"
                disabled={!!orderFilter}
                onClick={() => applyRange(daysAgo(preset.days))}
                className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                {preset.label}
              </button>
            ))}
          </div>

          {admin && (
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={adminOnly}
                onChange={(e) => {
                  setAdminOnly(e.target.checked);
                  setPage(1);
                }}
                className="h-3.5 w-3.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              관리자 전용만
            </label>
          )}

          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runSearch();
            }}
            placeholder="내용, 작성자, 보내는 분, 받는 분, 주문번호"
            className="min-w-[200px] flex-1 rounded-lg border px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={runSearch}
            className="rounded-lg bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-800"
          >
            검색
          </button>
          {query && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setQuery('');
                setPage(1);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              초기화
            </button>
          )}
          {/* 열 고르기 — 폭을 최대한 줄여도 화면마다 사정이 달라서, 마지막 조절은 사용자 몫 */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className={`rounded-lg border px-3 py-2 text-xs font-medium ${
                hiddenColumns.length > 0
                  ? 'border-gray-400 bg-gray-100 text-gray-700'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
              title="표에 띄울 열 고르기 (다음에 들어와도 유지됩니다)"
            >
              열{' '}
              {hiddenColumns.length > 0
                ? `${TOGGLE_COLUMNS.length - hiddenColumns.length}/${
                    TOGGLE_COLUMNS.length
                  }`
                : '전체'}
            </button>
            {showColumnMenu && (
              <>
                {/* 바깥을 누르면 닫힌다 */}
                <div
                  className="fixed inset-0 z-20"
                  onClick={() => setShowColumnMenu(false)}
                />
                <div className="absolute right-0 z-30 mt-1 w-40 rounded-lg border bg-white p-2 shadow-lg">
                  {TOGGLE_COLUMNS.map(({ key, label }) => (
                    <label
                      key={key}
                      className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={!hiddenColumns.includes(key)}
                        onChange={() => toggleColumn(key)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-cyan-700 focus:ring-cyan-600"
                      />
                      {label}
                    </label>
                  ))}
                  <p className="mt-1 border-t px-1.5 pt-1 text-[10px] leading-tight text-gray-400">
                    값이 하나도 없는 열은 켜도 안 뜹니다
                  </p>
                </div>
              </>
            )}
          </div>

          {/* 표를 줄글로 훑기 전에 "어디에 몰리나"부터 보는 입구 */}
          <button
            type="button"
            onClick={() => setShowStats(!showStats)}
            disabled={unavailable}
            className={`rounded-lg border px-3 py-2 text-xs font-medium disabled:opacity-50 ${
              showStats
                ? 'border-cyan-700 bg-cyan-700 text-white'
                : 'border-cyan-300 text-cyan-700 hover:bg-cyan-50'
            }`}
            title="조회 조건 전체(최대 2,000건)를 집계해 문의 유형·시간대·상조 분포를 보여줍니다"
          >
            {showStats ? '통계 닫기' : '통계'}
          </button>
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={isExporting || unavailable || total === 0}
            className="rounded-lg border border-emerald-300 px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            title="현재 조회 조건 그대로 엑셀로 내보내기"
          >
            {isExporting ? '내보내는 중...' : '엑셀'}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span>
            총 <span className="font-bold text-gray-800">{total}</span>건
          </span>
          <span className="text-gray-400">{admin ? '전 매장' : '내 매장'}</span>
          {isFetching && <span className="text-cyan-700">불러오는 중...</span>}
          {writerCounts.length > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-gray-400">{summaryScope}작성자</span>
              {writerCounts.map(([name, count]) => (
                <span
                  key={name}
                  className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600"
                >
                  {name} {count}
                </span>
              ))}
            </>
          )}
          {statusCounts.payment.length > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-gray-400">{summaryScope}결제</span>
              {statusCounts.payment.map(([label, count]) => (
                <span
                  key={label}
                  className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600"
                >
                  {label} {count}
                </span>
              ))}
            </>
          )}
          {statusCounts.delivery.length > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-gray-400">{summaryScope}배송</span>
              {statusCounts.delivery.map(([label, count]) => (
                <span
                  key={label}
                  className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600"
                >
                  {label} {count}
                </span>
              ))}
            </>
          )}
          {brandCounts.length > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-gray-400">{summaryScope}상조</span>
              {brandCounts.map(([name, count]) => (
                <span
                  key={name}
                  title={name}
                  className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600"
                >
                  {shortBrandName(name)} {count}
                </span>
              ))}
            </>
          )}
        </div>
      </div>

      {/* 통계 — 표 위에 둔다. 훑기 전에 결론부터 보고 필요한 조건으로 좁히는 순서 */}
      {showStats && !unavailable && (
        <div className="mt-4">
          <CsMemoLogStatsPanel
            stats={stats}
            isLoading={isStatsLoading}
            capped={allData?.capped ?? false}
            accent="cyan"
            statusTitle="배송 상태"
            onPickQuery={(q) => {
              setSearchInput(q);
              setQuery(q);
              setPage(1);
            }}
          />
        </div>
      )}

      {/* 목록 */}
      <div className="mt-4">
        {isLoading ? (
          <div className="center-box py-20">
            <Loading />
          </div>
        ) : unavailable ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center text-sm text-gray-400">
            CS 응대 로그가 아직 서버에 반영되지 않았습니다.
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center text-sm text-gray-400">
            {orderFilter
              ? '이 주문에 남긴 메모가 없습니다.'
              : '조회 기간에 남긴 메모가 없습니다.'}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border bg-white">
            {/* min-w를 못 박아야 좁은 화면에서 열을 짜부라뜨리지 않고 가로 스크롤로 넘어간다.
                min-w-full만 두면 표가 컨테이너에 억지로 맞춰지며 글자가 세로로 쪼개진다 */}
            <table className="w-full min-w-[1280px] text-sm">
              {/* 열 순서 = 로그를 읽는 순서.
                  "언제 · 어떤 상태(결제·배송) · 무슨 응대(내용)" 가 왼쪽에 모여야
                  쭉 훑으면서 문의 패턴이 잡힌다. 주문·상가·금액은 확인이 필요할 때 보는 값이라 뒤로.
                  결제와 배송은 **다른 축**이라 한 칸에 겹치지 않고 열을 나눴다 —
                  "결제취소인데 배송완료" 같은 조합이 실제로 파고들어야 하는 건이다. */}
              <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 shadow-[inset_0_-1px_0_0_#e5e7eb]">
                <tr>
                  {/* 순번 — 페이지를 넘겨도 이어진다(2페이지 첫 줄이 21번). "몇 번 건" 으로
                      서로 가리킬 수 있어야 응대 회의·매뉴얼 작성이 된다 */}
                  <th className="whitespace-nowrap px-3 py-3 font-medium">순번</th>
                  <th className="whitespace-nowrap px-3 py-2.5 font-medium">작성일시</th>
                  <th className="whitespace-nowrap px-3 py-2.5 font-medium">결제</th>
                  <th className="whitespace-nowrap px-3 py-2.5 font-medium">배송</th>
                  {/* 이 표의 본문. 매뉴얼 재료라 가장 넓고 가장 왼쪽에 가깝게 */}
                  <th className="px-3 py-2.5 font-medium">응대 내용</th>
                  {/* 문구 오기입·수정이 화환 CS의 큰 축이라 응대 내용 바로 옆에 둔다 */}
                  {columns.phrase && (
                    <th className="whitespace-nowrap px-3 py-2.5 font-medium">
                      리본 문구
                    </th>
                  )}
                  {columns.order && (
                    <th className="whitespace-nowrap px-3 py-2.5 font-medium">주문</th>
                  )}
                  {columns.deliveryPlace && (
                    <th className="whitespace-nowrap px-3 py-2.5 font-medium">상가</th>
                  )}
                  {/* 상조는 상가와 다른 축(어느 상조 건이냐)이라 열을 나눈다 */}
                  {columns.brand && (
                    <th className="whitespace-nowrap px-3 py-2.5 font-medium">상조</th>
                  )}
                  {/* 화환 퀄리티 문의는 사진을 봐야 답이 나온다 — 썸네일 클릭 시 원본 */}
                  {columns.photo && (
                    <th className="whitespace-nowrap px-3 py-2.5 font-medium">사진</th>
                  )}
                  {columns.amount && (
                    <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">
                      금액
                    </th>
                  )}
                  {/* '응대 내용' 열은 이 메모 한 건, 이 열은 그 주문에 쌓인 응대 흐름 전체로 가는 입구 */}
                  <th className="whitespace-nowrap px-3 py-2.5 font-medium">이력</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, index) => {
                  //* 최신순 목록이라 **가장 최근이 가장 큰 번호**(= 총 건수)이고 아래로 갈수록 줄어든다.
                  //* 새 메모가 쌓여도 과거 건의 번호가 안 밀리도록 총 건수에서 역산한다
                  const rowNo = total - ((page - 1) * limit + index);
                  const badge = shopOrderCsMemoAdapter.roleBadge(item.byRole);
                  //* 서버 memoCount가 있으면 그게 정답(전체 기준), 없으면 페이지 기준 근사
                  const serverMemoCount = item.order?.memoCount;
                  const memoCount =
                    serverMemoCount ?? pageMemoCountByOrder[item.shopOrder] ?? 1;
                  const cancel = cancelProgress(item.order ?? {});
                  const delivery = deliveryDateInfo(item.order ?? {});
                  const brandName = orderBrandName(item.order);
                  const amount = amountBreakdown(item.order ?? {});
                  const refundRoute = refundRouteLabel(item.order ?? {});
                  return (
                    <tr
                      key={item._id}
                      className={item.adminOnly ? 'bg-amber-50/50' : 'hover:bg-gray-50'}
                    >
                      <td className="whitespace-nowrap px-3 py-2 align-top text-xs tabular-nums text-gray-400">
                        {rowNo}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 align-top text-xs text-gray-500">
                        {fmtDateTime(item.createdAt)}
                      </td>
                      {/* 결제 — 배지와 결제일만 남긴다. "PG 취소 · 확정까지 2~3영업일" 같은
                          긴 문구를 셀에 두면 그 한 줄 때문에 열 전체가 늘어나서 클릭 툴팁으로 접었다 */}
                      <td className="whitespace-nowrap px-3 py-2 align-top text-xs">
                        {item.order?.paymentStatus ? (
                          <>
                            <span
                              className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                                paymentStatusBadge(item.order.paymentStatus).className
                              }`}
                            >
                              {paymentStatusBadge(item.order.paymentStatus).label}
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                        <CellDetailPopover
                          title="결제 상세"
                          lines={[
                            item.order?.purchasedAt && {
                              label: '결제일',
                              text: fmtDate(item.order.purchasedAt),
                            },
                            item.order?.method
                              ? { label: '수단', text: item.order.method }
                              : null,
                            amount ? { label: '금액', text: amount } : null,
                            /* 라벨은 반드시 cancelProgress()를 거친다 — canceledAt은 취소 "요청"
                               시각이라 그대로 쓰면 환불 안 나간 건도 환불됐다고 안내하게 된다 */
                            cancel && {
                              label: cancel.label,
                              text: fmtDate(cancel.date),
                              tone: (cancel.label === '환불완료' ? 'good' : 'danger') as
                                | 'good'
                                | 'danger',
                            },
                            /* 환불 경로는 "어디로 돈이 돌아가나" — 취소 건 응대의 첫 질문 */
                            cancel && refundRoute
                              ? { label: '환불', text: refundRoute }
                              : null,
                            item.order?.cancelReason
                              ? { label: '사유', text: item.order.cancelReason }
                              : null,
                          ]}
                        />
                        {item.order?.purchasedAt && (
                          <div className="mt-0.5 text-[11px] text-gray-400">
                            {fmtDate(item.order.purchasedAt)}
                          </div>
                        )}
                        {/* 환불이 실제로 나갔는지는 배지로 안 보인다 — 이것만 짧게 남긴다 */}
                        {cancel?.label === '환불완료' && (
                          <div className="text-[11px] text-emerald-600">환불완료</div>
                        )}
                      </td>
                      {/* 배송 — 결제와 다른 축이라 열을 나눴다. 라벨이 겹치므로 축을 앞에 붙인다 */}
                      <td className="whitespace-nowrap px-3 py-2 align-top text-xs">
                        {item.order?.deliveryStatus ? (
                          <>
                            <span
                              className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                                deliveryStatusBadge(item.order.deliveryStatus).className
                              }`}
                            >
                              {deliveryStatusBadge(item.order.deliveryStatus).label}
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                        {delivery && (
                          <div className="mt-0.5 text-[11px] text-gray-400">
                            {delivery.estimated ? '예정' : '완료'}{' '}
                            {fmtDate(delivery.date)}
                          </div>
                        )}
                      </td>
                      {/* 유일하게 늘어나는 열 — min-w가 없으면 다른 열에 밀려 폭 0까지 눌린다.
                          작성자는 본문에 붙여 읽는 게 자연스러워 열을 따로 두지 않는다 */}
                      <td className="min-w-[240px] max-w-[420px] px-3 py-2 align-top">
                        <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-800">
                          {/* 공개범위는 작성자가 아니라 이 메모 자체의 속성이라 본문 옆에 둔다.
                              행 배경(amber)이 뭘 뜻하는지도 여기서 설명된다 */}
                          {item.adminOnly && (
                            <span className="mr-1 inline-flex rounded bg-amber-200 px-1.5 py-0.5 align-middle text-[10px] font-semibold leading-tight text-amber-800">
                              관리자 전용
                            </span>
                          )}
                          {item.body}
                        </p>
                        <div className="mt-1 text-[11px] text-gray-400">
                          {item.byName}
                          {/* 이름이 이미 역할을 말하고 있으면(Admin 계정명 "관리자") 배지는 중복이라 뺀다 */}
                          {!isRoleBadgeRedundant(item.byName, badge.label) && (
                            <span
                              className={`ml-1 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${badge.className}`}
                            >
                              {badge.label}
                            </span>
                          )}
                        </div>
                      </td>
                      {columns.phrase && (
                        // 문구는 한 글자 차이로 리본을 다시 만들어야 해서 자르지 않는다.
                        // 열 폭을 고정해 두고 그 안에서 줄바꿈시킨다 (break-keep — 단어 단위로)
                        <td className="w-[150px] min-w-[150px] break-keep px-3 py-2 align-top text-xs">
                          {item.order?.senderPhrase || item.order?.condolencePhrase ? (
                            <>
                              {/* 리본에 실제로 박히는 이름 — 문의의 대부분이 이 줄이다 */}
                              {item.order.senderPhrase && (
                                <div className="text-gray-700">
                                  {item.order.senderPhrase}
                                </div>
                              )}
                              {item.order.condolencePhrase && (
                                <div className="text-gray-400">
                                  {item.order.condolencePhrase}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      )}
                      {columns.order && (
                        <td className="whitespace-nowrap px-3 py-2 align-top text-xs">
                          {item.order ? (
                            <>
                              <div
                                className="text-gray-700"
                                title={item.order.senderName}
                              >
                                {clipSenderName(item.order.senderName) || '-'}
                                <span className="mx-1 text-gray-300">→</span>
                                {bareReceiverName(item.order.receiverName) || '-'}
                              </div>
                              {item.order.itemName && (
                                <div className="text-gray-500">{item.order.itemName}</div>
                              )}
                              {/* 응대 건수는 '이력 보기' 버튼 옆에 있다 — 눌렀을 때 보이는 개수라
                                여기 두면 주문 정보의 일부처럼 읽힌다 */}
                              <div className="text-[11px] text-gray-400">
                                <button
                                  type="button"
                                  onClick={() => filterByOrder(item)}
                                  className="rounded text-gray-400 underline decoration-dotted hover:text-cyan-700"
                                  title="이 주문의 응대 이력만 모아보기 (기간 무시)"
                                >
                                  #{shortOrderNo(item)}
                                </button>
                              </div>
                            </>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      )}
                      {columns.deliveryPlace && (
                        // break-keep — 장례식장 이름이 좁아졌을 때 글자 단위로 끊기지 않게
                        <td className="min-w-[130px] break-keep px-3 py-2 align-top text-xs">
                          {item.order &&
                          (item.order.deceasedName ||
                            item.order.funeralHomeName ||
                            item.order.roomName) ? (
                            <>
                              {/* 고인명이 전화 응대의 1순위 식별 키라 첫 줄에 둔다 */}
                              {item.order.deceasedName && (
                                <div className="text-gray-700">
                                  {DeathCharacter} {item.order.deceasedName}님
                                </div>
                              )}
                              {deliveryPlaceLine(item.order) && (
                                <div className="text-gray-500">
                                  {deliveryPlaceLine(item.order)}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      )}
                      {/* 상조 — 긴 이름은 닉네임/축약, 전체명은 title로.
                          상조를 잘못 짚으면 응대가 통째로 틀어진다 */}
                      {columns.brand && (
                        <td className="whitespace-nowrap px-3 py-2 align-top text-xs">
                          {brandName ? (
                            <span
                              className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600"
                              title={brandName}
                            >
                              {shortBrandName(brandName)}
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      )}
                      {columns.photo && (
                        <td className="whitespace-nowrap px-3 py-2 align-top">
                          {item.order?.deliveredImageUrl ? (
                            //* 목록엔 축소본(w400)만 받고, 확대는 원본으로 — 표 스크롤이 무거워지지 않게
                            //* (축소본이 없는 옛 이미지는 BucketImage가 원본으로 되돌린다)
                            <BucketImage
                              value={item.order.deliveredImageUrl}
                              width={44}
                              height={44}
                              className="rounded border object-cover"
                              alt="배송완료 사진"
                            />
                          ) : (
                            <span className="text-xs text-gray-300">-</span>
                          )}
                        </td>
                      )}
                      {columns.amount && (
                        <td className="whitespace-nowrap px-3 py-2 text-right align-top text-xs">
                          {item.order?.amount === undefined ? (
                            <span className="text-gray-300">-</span>
                          ) : (
                            <>
                              <div className="font-medium text-gray-700">
                                {formatPrice(item.order.amount)}원
                              </div>
                              {/* 단가×수량과 어긋나는 정당한 사유 — 경위는 모달에서 */}
                              {item.order.hasItemChange && (
                                <div className="text-[10px] text-amber-600">
                                  상품 교체
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-3 py-2 align-top">
                        {item.shopOrder ? (
                          <div className="flex items-center gap-1.5">
                            {/* 건수를 버튼 안에 넣으면 행마다 버튼 폭이 달라져 어색하다 —
                                버튼은 고정 크기로 두고 건수는 옆에 따로 */}
                            <button
                              type="button"
                              onClick={() => setModalItem(item)}
                              className="rounded-md border border-cyan-200 px-2 py-1 text-[11px] font-medium text-cyan-700 hover:bg-cyan-50"
                              title="이 주문의 응대 이력 전체 보기 · 이어서 남기기"
                            >
                              이력
                            </button>
                            {/* 2건 이상일 때만 — 1건은 굳이 알릴 필요가 없다 */}
                            {memoCount > 1 && (
                              <span
                                className="whitespace-nowrap text-[11px] font-semibold text-cyan-700"
                                title={
                                  serverMemoCount === undefined
                                    ? '이 주문에 쌓인 응대 건수 (지금 보고 있는 페이지 기준)'
                                    : '이 주문의 전체 응대 건수'
                                }
                              >
                                {memoCount}건
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[11px] text-gray-300">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 페이지 이동 */}
        {!unavailable && total > 0 && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>페이지당</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="rounded-lg border px-2 py-1 text-xs"
              >
                {[20, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}건
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                이전
              </button>
              <span className="text-xs text-gray-500">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>

      {modalItem?.shopOrder && (
        <CsMemoModalBase
          adapter={shopOrderCsMemoAdapter}
          title="주문 응대 이력"
          subtitle={orderSummaryLine(modalItem)}
          {...(modalItem.order?.deliveredImageUrl
            ? {
                //* 퀄리티 문의는 사진을 보면서 응대한다 — 모달에서는 크게
                headerExtra: (
                  <BucketImage
                    value={modalItem.order.deliveredImageUrl}
                    width={120}
                    className="rounded border object-cover"
                    alt="배송완료 사진"
                  />
                ),
              }
            : {})}
          target={{ shopOrder: modalItem.shopOrder }}
          onClose={() => setModalItem(null)}
          zIndexClassName="z-50"
        />
      )}
    </div>
  );
};

export default SellerCsMemoLogPage;
